import React, { useEffect } from 'react';
import { FaBell, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import PlanCard from '../PlanCard/PlanCard';
import { GoldStar, SilverStar } from '../PlanCard/PlanCard';
import './PlanExpiryWarningModal.scss';

// Days before expiry that trigger the warning popup
const WARNING_DAYS = [15, 7, 6, 5, 4, 3, 2, 1];

/**
 * PlanExpiryWarningModal - Shows a renewal warning for Silver/Gold plan users
 * when their plan expires in 15, 7, 6, 5, 4, 3, 2, or 1 day(s).
 *
 * @param {boolean}  isOpen       - Whether the modal is visible
 * @param {function} onClose      - Callback to close the modal
 * @param {number}   daysRemaining - Days left on the current paid plan
 * @param {string}   planTier     - 'silver' | 'gold'
 * @param {string}   userId       - Used to namespace the localStorage key
 */
const PlanExpiryWarningModal = ({
  isOpen,
  onClose,
  daysRemaining,
  planTier = 'silver',
  userId,
}) => {
  // Listen for closePlan event dispatched by PlanCard after a plan action
  useEffect(() => {
    const handleClosePlan = () => {
      if (isOpen && onClose) {
        onClose(true);
      }
    };
    window.addEventListener('closePlan', handleClosePlan);
    return () => window.removeEventListener('closePlan', handleClosePlan);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isLastDay = daysRemaining === 1;
  const isCritical = daysRemaining <= 3;
  const isWarning = daysRemaining <= 7 && daysRemaining > 3;

  const urgencyClass = isCritical
    ? 'plan-expiry-modal--critical'
    : isWarning
      ? 'plan-expiry-modal--warning'
      : '';

  const planLabel =
    planTier.charAt(0).toUpperCase() + planTier.slice(1).toLowerCase();

  const headingText = isCritical
    ? `Your ${planLabel} Plan Expires ${isLastDay ? 'Tomorrow!' : `in ${daysRemaining} Days!`}`
    : `Your ${planLabel} Plan Expires in ${daysRemaining} Days`;

  const subText = isCritical
    ? 'Renew now to avoid losing access to your features and data.'
    : daysRemaining === 15
      ? 'Your plan is approaching its expiry. Renew early to ensure uninterrupted access.'
      : 'Renew your plan to keep using all platform features without interruption.';

  return (
    <div className="plan-expiry-overlay" onClick={onClose}>
      <div
        className={`plan-expiry-modal ${urgencyClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          className="plan-expiry-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          <FaTimes />
        </button>

        {/* Header */}
        <div className="plan-expiry-modal__header">
          <div className="plan-expiry-modal__header-content">
            <div className="plan-expiry-modal__icon-wrapper">
              {isCritical ? (
                <FaExclamationTriangle className="plan-expiry-modal__icon" />
              ) : (
                <FaBell className="plan-expiry-modal__icon" />
              )}
            </div>
            <div className="plan-expiry-modal__header-text">
              <h2 className="plan-expiry-modal__title">{headingText}</h2>
              <p className="plan-expiry-modal__subtitle">{subText}</p>
            </div>
          </div>

          {/* Days counter */}
          <div className="plan-expiry-modal__days-container">
            <div
              className={`plan-expiry-modal__days-circle${isCritical ? ' plan-expiry-modal__days-circle--critical' : ''}`}
            >
              <span className="plan-expiry-modal__days-number">
                {daysRemaining}
              </span>
              <span className="plan-expiry-modal__days-label">
                {daysRemaining === 1 ? 'Day Left' : 'Days Left'}
              </span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="plan-expiry-modal__body">
          <p className="plan-expiry-modal__lead">
            Renew or upgrade your plan to continue using all features
          </p>

          <div className="plan-expiry-modal__plans">
            <PlanCard tier="Silver" />
            <PlanCard tier="Gold" highlighted />
          </div>

          <div className="plan-expiry-modal__features-note">
            <div className="plan-expiry-modal__feature-item">
              <SilverStar />
              <span>Silver – Perfect for individuals and small teams</span>
            </div>
            <div className="plan-expiry-modal__feature-item">
              <GoldStar />
              <span>Gold – Best for growing businesses with advanced needs</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="plan-expiry-modal__footer">
          <button className="plan-expiry-modal__skip-btn" onClick={onClose}>
            Remind Me Later
          </button>
        </div>
      </div>
    </div>
  );
};

export { WARNING_DAYS };
export default PlanExpiryWarningModal;
